import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, CreditCard, Receipt, Star, Check, X, Confetti, Clock, WarningCircle, Phone, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl, formatINR, formatApiError } from "@/lib/api";
import { payForBooking, payBalanceForBooking } from "@/lib/payment";
import { Spinner } from "@/components/Loaders";
import { ReviewDialog } from "@/components/ReviewDialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BOOKING_STATUS_STYLE, fmtStatus } from "@/constants/status";
import { useSiteInfo } from "@/lib/site";

export default function BookingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const info = useSiteInfo();
  const [b, setB] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [cancelPreview, setCancelPreview] = useState(null);

  const load = () => api.get(`/bookings/${id}`).then((r) => setB(r.data)).catch(() => toast.error("Not found"));
  useEffect(() => { load(); }, [id]);

  if (!b) return <div className="flex h-screen items-center justify-center"><Spinner /></div>;

  const paid = b.payment_status === "paid";
  const awaiting = ["awaiting_payment", "payment_failed"].includes(b.status);
  const canCancel = ["pending", "awaiting_payment", "confirmed", "payment_failed", "payment_expired"].includes(b.status);
  const num = (v, alt) => (v != null ? v : b[alt]) || 0;

  const openCancel = async () => {
    try {
      const r = await api.get(`/bookings/${id}/cancellation-preview`);
      setCancelPreview(r.data);
    } catch { setCancelPreview({ eligible: false, refund_amount: 0, refund_pct: 0, reason: "" }); }
  };

  const doCancel = async () => {
    try {
      await api.patch(`/bookings/${id}/cancel`);
      toast.success("Booking cancelled");
      setCancelPreview(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const Row = ({ label, value, strong }) => (
    <div className="flex justify-between border-b border-stone-100 py-2.5 text-sm">
      <span className="text-stone-500">{label}</span>
      <span className={strong ? "font-heading font-bold text-primary" : "font-medium text-stone-800"}>{value}</span>
    </div>
  );

  const steps = [
    { key: "requested", label: "Booking Requested" },
    { key: "accepted", label: "Owner Accepted" },
    { key: "payment", label: "Advance Payment" },
    { key: "verified", label: "Payment Verified" },
    { key: "confirmed", label: "Booking Confirmed" },
    { key: "completed", label: "Event / Stay Completed" },
  ];
  const done = {
    requested: true,
    accepted: ["awaiting_payment", "confirmed", "completed"].includes(b.status),
    payment: paid,
    verified: paid,
    confirmed: ["confirmed", "completed"].includes(b.status),
    completed: b.status === "completed",
  };
  const isDead = ["rejected", "cancelled", "refund_pending", "refunded", "payment_expired"].includes(b.status);

  return (
    <div className="px-4 pb-10 pt-5">
      <button data-testid="bd-back" onClick={() => navigate(-1)} className="mb-3 flex items-center gap-1 text-sm text-stone-600"><ArrowLeft size={18} /> Back</button>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-semibold text-stone-900">Booking Details</h1>
        <span data-testid="bd-status" className={`rounded-full px-3 py-1 text-xs font-semibold ${BOOKING_STATUS_STYLE[b.status]}`}>{fmtStatus(b.status)}</span>
      </div>

      {/* Confirmation banner */}
      {paid && b.status === "confirmed" && (
        <div data-testid="bd-confirmed-banner" className="mt-4 rounded-2xl border border-green-200 bg-green-50 p-5 text-center">
          <Confetti size={34} weight="fill" className="mx-auto text-green-600" />
          <p className="mt-2 font-heading text-lg font-bold text-green-800">Booking Confirmed</p>
          <p className="font-mono text-sm text-green-700">{b.booking_ref}</p>
          <div className="mt-3 flex justify-center gap-6 text-sm">
            <div><p className="text-stone-500">Advance Paid</p><p className="font-heading font-bold text-green-700">{formatINR(num(b.advance_amount))}</p></div>
            <div><p className="text-stone-500">{b.balance_paid ? "Balance Paid" : "Balance Due"}</p><p className={`font-heading font-bold ${b.balance_paid ? "text-green-700" : "text-stone-800"}`}>{formatINR(num(b.balance_amount))}</p></div>
          </div>
        </div>
      )}

      {/* Awaiting payment CTA */}
      {awaiting && (
        <div data-testid="bd-awaiting-banner" className="mt-4 rounded-2xl border border-orange-200 bg-orange-50 p-5">
          <p className="flex items-center gap-2 font-heading font-semibold text-orange-800"><Clock size={18} weight="fill" /> Pay advance to confirm</p>
          <p className="mt-1 text-sm text-orange-700">Your booking has been accepted. Please pay the booking advance to confirm your reservation.</p>
        </div>
      )}

      <div className="mt-4 overflow-hidden rounded-2xl border border-stone-200 bg-white">
        <img src={fileUrl(b.property_image)} alt={b.property_name} className="h-40 w-full object-cover" />
        <div className="p-4">
          <p className="font-heading text-lg font-semibold">{b.property_name}</p>
          <p className="flex items-center gap-1 text-sm text-stone-500"><MapPin size={14} weight="fill" className="text-primary" /> {b.property_location}</p>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-stone-200 bg-white p-5" data-testid="booking-timeline">
        <h2 className="mb-4 font-heading font-semibold">Booking Timeline</h2>
        {isDead ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white"><Check size={14} weight="bold" /></span><span className="text-sm font-medium text-stone-800">Booking Requested</span></div>
            <div className="flex items-center gap-3"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white"><X size={14} weight="bold" /></span><span className="text-sm font-medium text-red-600">{fmtStatus(b.status)}</span></div>
            {["refund_pending", "refunded"].includes(b.status) && (
              <div className="flex items-center gap-3"><span className={`flex h-6 w-6 items-center justify-center rounded-full text-white ${b.status === "refunded" ? "bg-teal-500" : "bg-purple-400"}`}><Check size={14} weight="bold" /></span><span className="text-sm font-medium text-stone-700">{b.status === "refunded" ? `Refunded ${formatINR(num(b.refund_amount))}` : `Refund of ${formatINR(num(b.refund_amount))} processing`}</span></div>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            {steps.map((s, i) => (
              <div key={s.key} className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${done[s.key] ? "bg-primary text-white" : "bg-stone-200 text-stone-500"}`}>
                    {done[s.key] ? <Check size={13} weight="bold" /> : i + 1}
                  </span>
                  {i < steps.length - 1 && <span className={`h-6 w-0.5 ${done[steps[i + 1].key] ? "bg-primary" : "bg-stone-200"}`} />}
                </div>
                <span className={`pt-0.5 text-sm ${done[s.key] ? "font-medium text-stone-800" : "text-stone-400"}`}>{s.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 rounded-2xl border border-stone-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <p className="font-mono text-sm font-semibold text-primary">{b.booking_ref}</p>
        </div>
        <div className="mt-3">
          <Row label="Date" value={b.date} />
          <Row label="Guests" value={b.guests} />
          <Row label="Booking type" value={b.booking_type} />
          <Row label="Package" value={b.package} />
          <Row label="Customer" value={b.customer_name} />
          <Row label="Phone" value={b.customer_phone} />
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-stone-200 bg-white p-5">
        <h2 className="font-heading font-semibold">Price Details</h2>
        <div className="mt-2">
          <Row label="Base amount" value={formatINR(num(b.base_amount, "base_price"))} />
          {num(b.service_fee) > 0 && <Row label="Service fee" value={formatINR(num(b.service_fee))} />}
          <Row label={`GST (${b.gst_rate || 18}%)`} value={formatINR(num(b.tax_amount, "taxes"))} />
          <Row label="Total booking value" value={formatINR(b.total_amount)} strong />
          <div className="mt-2 rounded-xl bg-accent/50 p-3">
            <Row label={`Advance (${b.advance_percentage || 25}%)`} value={formatINR(num(b.advance_amount))} />
            <div className="flex justify-between pt-1 text-sm"><span className="text-stone-500">Balance (at venue)</span><span className="font-medium text-stone-800">{formatINR(num(b.balance_amount))}</span></div>
          </div>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {awaiting && (
          <>
            <Button data-testid="bd-pay" onClick={() => payForBooking(b.id, (data) => (data ? navigate(`/booking-confirmed/${b.id}`) : load()))} className="w-full gap-2 rounded-xl bg-primary py-6 text-base hover:bg-green-900">
              <CreditCard size={18} /> {b.status === "payment_failed" ? "Retry — Pay" : "Pay"} {formatINR(num(b.advance_amount))} Advance
            </Button>
            {info.razorpay_link && (
              <a data-testid="bd-pay-razorpay-link" href={info.razorpay_link} target="_blank" rel="noopener noreferrer" onClick={() => toast.info(`On the Razorpay page, pay ${formatINR(num(b.advance_amount))} and mention your ref ${b.booking_ref}.`)} className="flex w-full items-center justify-center gap-2 rounded-xl border border-primary/40 bg-accent/40 py-3 text-sm font-semibold text-primary hover:bg-accent">
                <LinkSimple size={16} /> Pay via Razorpay link (UPI / Cards)
              </a>
            )}
            <p className="text-center text-xs text-stone-400">Cancellation policy applies. Pay before your reservation window closes.</p>
          </>
        )}
        {paid && b.status === "confirmed" && num(b.balance_amount) > 0 && !b.balance_paid && (
          <>
            <Button data-testid="bd-pay-balance" onClick={() => payBalanceForBooking(b.id, load)} className="w-full gap-2 rounded-xl bg-primary py-6 text-base hover:bg-green-900"><CreditCard size={18} /> Pay Balance {formatINR(num(b.balance_amount))}</Button>
            {info.razorpay_link && (
              <a data-testid="bd-pay-balance-razorpay-link" href={info.razorpay_link} target="_blank" rel="noopener noreferrer" onClick={() => toast.info(`On the Razorpay page, pay ${formatINR(num(b.balance_amount))} and mention your ref ${b.booking_ref}.`)} className="flex w-full items-center justify-center gap-2 rounded-xl border border-primary/40 bg-accent/40 py-3 text-sm font-semibold text-primary hover:bg-accent"><LinkSimple size={16} /> Pay via Razorpay link (UPI / Cards)</a>
            )}
          </>
        )}
        {paid && b.balance_paid && (
          <div data-testid="bd-balance-paid" className="rounded-xl bg-green-50 py-3 text-center text-sm font-semibold text-green-700">Balance fully paid ✓</div>
        )}
        {paid && (
          <Button data-testid="bd-invoice" variant="outline" onClick={() => navigate(`/invoice/${b.id}`)} className="w-full gap-2 rounded-xl py-6 text-base"><Receipt size={18} /> Download Receipt</Button>
        )}
        {paid && b.status === "confirmed" && b.customer_phone && (
          <a href={`tel:${b.customer_phone}`} className="flex w-full items-center justify-center gap-2 rounded-xl border border-stone-200 py-3 text-sm font-medium text-stone-700 hover:bg-accent" data-testid="bd-contact"><Phone size={16} /> Contact Property</a>
        )}
        {b.status === "completed" && !b.reviewed && (
          <Button data-testid="bd-review" variant="outline" onClick={() => setShowReview(true)} className="w-full gap-2 rounded-xl py-6 text-base text-amber-600"><Star size={18} /> Write a Review</Button>
        )}
        {canCancel && (
          <Button data-testid="bd-cancel" variant="outline" className="w-full rounded-xl text-destructive hover:bg-red-50" onClick={openCancel}>Cancel Booking</Button>
        )}
      </div>

      <Dialog open={!!cancelPreview} onOpenChange={(v) => !v && setCancelPreview(null)}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><WarningCircle size={20} className="text-amber-500" /> Cancel this booking?</DialogTitle></DialogHeader>
          {cancelPreview && (
            <div className="text-sm text-stone-600">
              {cancelPreview.eligible ? (
                <p>You will receive a refund of <span className="font-heading font-bold text-primary">{formatINR(cancelPreview.refund_amount)}</span> ({cancelPreview.refund_pct}% of your advance). {cancelPreview.reason}</p>
              ) : (
                <p>{cancelPreview.reason || "No refund is applicable per the cancellation policy."}</p>
              )}
              {cancelPreview.policy && (
                <div className="mt-3 rounded-xl bg-stone-50 p-3 text-xs text-stone-500">
                  <p className="mb-1 font-semibold text-stone-600">Cancellation Policy</p>
                  <p>{cancelPreview.policy.full_refund_days}+ days before: 100% refund</p>
                  <p>{cancelPreview.policy.partial_refund_days}–{cancelPreview.policy.full_refund_days - 1} days: {cancelPreview.policy.partial_refund_pct}% refund</p>
                  <p>Within {cancelPreview.policy.no_refund_hours} hours: no refund</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setCancelPreview(null)}>Keep Booking</Button>
            <Button data-testid="confirm-cancel" className="rounded-xl bg-destructive hover:bg-red-700" onClick={doCancel}>Confirm Cancellation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReviewDialog booking={b} open={showReview} onOpenChange={setShowReview} onDone={load} />
    </div>
  );
}
